import { useCallback, useEffect, useState } from 'react';
import {
  invalidateManagedDisplayCache,
  isSignedDisplayUrl,
  msUntilViewPathExpires,
  resolveImmediateSignedSrc,
  resolveSignedDisplayUrl,
  sanitizeManagedDisplayResult,
  shouldRotateDisplayImageSrc,
  subscribeDisplayCacheUpdate,
} from '@/utils/imageDisplayAccess';
import { staggerDelayMsForKey } from '@/utils/cacheExpiryJitter';

function buildSignedSrcRequestKey(rawUrl, options = {}) {
  const displayOverride = options.displayUrl || options.viewUrl || '';
  const storedUrlDisplay = options.storedUrlDisplay || '';
  const imageId = options.imageId != null ? String(options.imageId) : '';
  const variant = String(options.variant || '').trim();
  return `${String(rawUrl || '').trim()}\0${displayOverride}\0${storedUrlDisplay}\0${imageId}\0${variant}`;
}

/**
 * 异步解析受管存储签名读图地址（local/oss 统一），供 <img src> 使用。
 * 首帧同步读 viewUrl / session 缓存；sign-batch 完成后通过订阅刷新，无需整表 remount。
 * 支持 HMAC view 与 OSS 预签名直链（client-direct-url）到期前自动换签。
 * @param {string} rawUrl - 持久化地址（/uploads/{root}/… 或 OSS https）
 * @param {{ displayUrl?: string, viewUrl?: string, imageId?: string|number, variant?: string }} [options]
 */
export function useSignedImageSrc(rawUrl, options = {}) {
  const displayOverride = options.displayUrl || options.viewUrl || '';
  const storedUrlDisplay = options.storedUrlDisplay || '';
  const imageId = options.imageId != null ? String(options.imageId) : '';
  const variant = options.variant || '';
  const requestKey = buildSignedSrcRequestKey(rawUrl, {
    displayUrl: displayOverride,
    storedUrlDisplay,
    imageId,
    variant,
  });

  const [state, setState] = useState(() => ({
    key: requestKey,
    src: resolveImmediateSignedSrc(rawUrl, options) || '',
  }));

  // URL/选项切换时同步清空旧图地址，避免首帧仍返回上一张的签名 URL
  if (state.key !== requestKey) {
    setState({
      key: requestKey,
      src: resolveImmediateSignedSrc(rawUrl, options) || '',
    });
  }

  const src = state.key === requestKey ? state.src : (resolveImmediateSignedSrc(rawUrl, options) || '');

  const setSrcIfNeeded = useCallback((next) => {
    const normalized = String(next || '').trim();
    setState((current) => {
      if (current.key !== requestKey) return current;
      if (!normalized) return current;
      if (!shouldRotateDisplayImageSrc(current.src, normalized)) return current;
      return { key: requestKey, src: normalized };
    });
  }, [requestKey]);

  const resetSrc = useCallback((next) => {
    setState({
      key: requestKey,
      src: String(next || '').trim(),
    });
  }, [requestKey]);

  // 仅同步重读本图缓存；勿在全局 sign-batch 完成时对所有图重新 async sign（会引发无限请求）
  useEffect(() => subscribeDisplayCacheUpdate(() => {
    const trimmed = String(rawUrl || '').trim();
    setSrcIfNeeded(resolveImmediateSignedSrc(trimmed, options));
  }), [rawUrl, displayOverride, storedUrlDisplay, imageId, variant, setSrcIfNeeded]);

  // 签名 URL 到期前自动 invalidate + 重新 sign，避免长时间停留页面后批量 410
  useEffect(() => {
    let alive = true;
    const trimmed = String(rawUrl || '').trim();
    const currentSrc = String(src || '').trim();
    if (!currentSrc || !isSignedDisplayUrl(currentSrc)) return undefined;

    const scheduleRefresh = async () => {
      invalidateManagedDisplayCache(trimmed, options);
      const resolved = await resolveSignedDisplayUrl(trimmed, options);
      const safe = sanitizeManagedDisplayResult(resolved, trimmed);
      if (alive) setSrcIfNeeded(safe || resolveImmediateSignedSrc(trimmed, options));
    };

    const delay = msUntilViewPathExpires(currentSrc);
    if (delay <= 0) {
      const burstSpread = staggerDelayMsForKey(trimmed || currentSrc, 90_000);
      window.setTimeout(() => {
        if (alive) void scheduleRefresh();
      }, burstSpread);
      return () => {
        alive = false;
      };
    }

    const burstSpread = staggerDelayMsForKey(trimmed || currentSrc, 90_000);
    const timer = window.setTimeout(() => {
      void scheduleRefresh();
    }, delay + burstSpread);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [src, rawUrl, displayOverride, storedUrlDisplay, imageId, variant, setSrcIfNeeded]);

  useEffect(() => {
    let alive = true;
    const preferred = String(displayOverride || '').trim();
    if (preferred.startsWith('data:') || preferred.startsWith('blob:')) {
      resetSrc(preferred);
      return () => {
        alive = false;
      };
    }
    const trimmed = String(rawUrl || '').trim();
    const immediate = resolveImmediateSignedSrc(trimmed, options) || '';
    // 切换目标图时强制重置；空值也要写回去，避免继续暴露上一张
    resetSrc(immediate);
    if (!trimmed) {
      return () => {
        alive = false;
      };
    }
    if (
      trimmed.startsWith('data:')
      || trimmed.startsWith('blob:')
      || trimmed.startsWith('indexeddb://')
    ) {
      resetSrc(trimmed);
      return () => {
        alive = false;
      };
    }
    if (immediate) {
      return () => {
        alive = false;
      };
    }
    (async () => {
      const resolved = await resolveSignedDisplayUrl(trimmed, options);
      const safe = sanitizeManagedDisplayResult(resolved, trimmed);
      if (alive) setSrcIfNeeded(safe || resolveImmediateSignedSrc(trimmed, options));
    })();
    return () => {
      alive = false;
    };
  }, [rawUrl, displayOverride, storedUrlDisplay, imageId, variant, resetSrc, setSrcIfNeeded]);

  return src;
}
